import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';

import { Router } from '@angular/router';
import { catchError } from 'rxjs/operators';
import { AppInitService } from 'src/app/app-init.service';
import { AppConfigurations } from 'src/app/shared/models/appConfiguration.model';
import { LocationDropDownModel } from 'src/app/shared/models/locations-drop-down-list.model';
import { LocationUsersDropDownModel } from 'src/app/shared/models/users-drop-down-list.model';
import { ToastService } from 'src/app/shared/services/toast.service';
import { Location } from '../models/location';
import { LocationCreatedResponse } from '../models/locationCreatedResponse';
import { LocationGridRequestModel } from '../models/locationGridRequestModel';
import { LocationRelationship } from '../models/locationRelationship';
import { LocationTypes } from '../models/locationTypes';
import { OperatingHours } from '../models/operatingHours';

import { SignedUserLocations } from 'src/app/admin/product-services/shared/models/signed-user-locations';
import { LocationSelector } from 'src/app/shared/models/location-selector/location-selector-model';

declare const window: any;

@Injectable({
  providedIn: 'root'
})
export class LocationService {

  private baseUrl: string = '';
  private apiController: string = '';
  private appConfig!: AppConfigurations;
  private locationSubject = new BehaviorSubject({} as Location);
  public locationData = this.locationSubject.asObservable();
  private locationHierarchySubject = new BehaviorSubject([] as number[]);
  locationHierarchy = this.locationHierarchySubject.asObservable();

  private pageMode: "Add" | "Edit" = "Add";
  public fromPS: Boolean = false;
  public fromH: boolean = false;
  public savedLocation: Location | null = null;

  constructor(private http: HttpClient,
    private appInitService: AppInitService,
    private router: Router,
    private toastService: ToastService) {
    appInitService.getAppConfiguration$().subscribe({
      next: (config) => this._onReceiveAppConfig(config)
    })
  }

  private _onReceiveAppConfig(appConfig: AppConfigurations) {
    this.appConfig = appConfig;
    this.baseUrl = this.appConfig.adminApi.baseApiUrl;
    this.apiController = this.appConfig.adminApi.locationController;
  }

  setSavedLocation(location: Location) {

    // TODO: Refactor the saving logic
    // in the main component.

    if (!location.services) {
      throw Error('location.services is undefined, which was not expected!');
    }
    location.services =
    location.services.map(s => {
      return {
        "locationId": s.locationId,
        "optIn": s.optIn,
        "serviceId": s.serviceId }
      });

    this.savedLocation = location;
    this.locationSubject.next(location);
  }

  setSubLocations(locationIds: number[]) {
    this.locationHierarchySubject.next(locationIds);
  }

  getSavedLocation(): Location | null {
    return this.savedLocation;
  }

  resetSavedLocation(): void {
    this.savedLocation = null;
  }

  setPageMode(pageMode: "Add" | "Edit"): void {
    this.pageMode = pageMode;
  }

  getPageMode(): "Add" | "Edit" {
    return this.pageMode;
  }

  successToast(message: string): void {
    this.toastService.showSuccess(message);
  }

  errorToast(message: string): void {
    this.toastService.showError(message);
  }

  getLocationByCode(locationCode: string): Observable<Location> {
    const queryParams = this.encodeLocationCode(locationCode);
    return this.http.get<Location>(`${this.baseUrl}${this.apiController}`, { params: queryParams });
  }

  encodeLocationCode(locationCode: string) {
    const encodeLocationCodes = encodeURIComponent(locationCode);
    let queryParams = new HttpParams();
    return queryParams = queryParams.set("locationCode", encodeLocationCodes.valueOf().toString())
  }

  getSubLocations(locationId: number, locationTypes?: LocationTypes, activeOnly: boolean = false): Observable<LocationRelationship[]> {
    let queryParams = new HttpParams();
    if (locationTypes == null) {
      queryParams = queryParams
        .set("activeOnly", activeOnly.valueOf().toString())
    } else {
      queryParams = queryParams
        .set("locationType", locationTypes.valueOf().toString())
        .set("activeOnly", activeOnly.valueOf().toString())
    }

    return this.http.get<LocationRelationship[]>(`${this.baseUrl}${this.apiController}/${locationId}/sublocations`,
      { params: queryParams })
      .pipe(
        catchError(this.handleError<LocationRelationship[]>('getSubLocations', []))
      );
  }

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      console.error(error);
      return of(result as T);
    };
  }

  /**
   * Retreive all locations as a tree view
   * @returns all locations as a tree view
   */
  getLocationsHierarchy(): Observable<LocationDropDownModel[]> {
    return this.http.get<LocationDropDownModel[]>(`${this.baseUrl}${this.apiController}/locationHierarchy`, {});
  }

  /**
   * Retreive locations as root locations list
   * @param rootLocations retreving root locations (-1 for any location as root)
   * @returns list of locations
   */
  getHierarchyRootLocations(rootLocations: any[]): Observable<LocationDropDownModel[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('locationIds', rootLocations.toString());

    return this.http.get<LocationDropDownModel[]>(`${this.baseUrl}${this.apiController}/locationRoot`, { params: queryParams });
  }

  /**
   * Retreive child locations for given parent location
   * @param rootLocation parent location
   * @returns list of child locations
   */
  getHierarchyChildLocations(rootLocation: string): Observable<LocationDropDownModel[]> {
    return this.http.get<LocationDropDownModel[]>(`${this.baseUrl}${this.apiController}/locationChild/${rootLocation}`, {});
  }

  /**
   * Retrieve user location hierarchy
   * @returns Nested user-location hierarchy
   */
  getNestedLocationUserHierarchy(): Observable<LocationUsersDropDownModel[]> {
    return this.http.get<LocationUsersDropDownModel[]>(`${this.baseUrl}${this.apiController}/nestedLocationUserHierarchy`);
  }

  /**
  * Retreive locations as root locations list
  * @param rootLocations retreving root locations (-1 for any location as root)
  * @returns list of locations
  */
  getHierarchyRootLocationUser(rootLocations: any[]): Observable<LocationUsersDropDownModel[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('locationIds', rootLocations.toString());

    return this.http.get<LocationUsersDropDownModel[]>(`${this.baseUrl}${this.apiController}/locationUserRoot`, { params: queryParams });
  }

  /**
   * Retreive child locations for given parent location
   * @param rootLocation parent location
   * @param searchKeyWord Search Keyword
   * @returns list of child locations
   */
  getHierarchyChildLocationUsers(rootLocation: string, searchKeyWord: string): Observable<LocationUsersDropDownModel[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('searchTerm', searchKeyWord.toString());
    return this.http.get<LocationUsersDropDownModel[]>(`${this.baseUrl}${this.apiController}/locationUserChild/${rootLocation}`, { params: queryParams });
  }

  /**
   * Retreive filtered location/ user heighrarchy
   * @param term Search term
   * @returns list of location/ user heighrarchy
   */
  getHierarchySearch(term: string): Observable<any[]> {
    let queryParams = new HttpParams();
    queryParams = queryParams.set('searchTerm', term.toString());
    return this.http.get<LocationDropDownModel[]>(`${this.baseUrl}${this.apiController}/searchLocationUser`, { params: queryParams });
  }

  getLocations(locationId: number, gridFilterData: LocationGridRequestModel): Observable<any> {
    const url = `${this.baseUrl}${this.apiController}/${locationId}/All?status=${gridFilterData.Status}&type=${gridFilterData.LocationType}
    &sortInfo.sortColumn=${gridFilterData.SortInfo.SortColumn}&sortInfo.sortDirection=${gridFilterData.SortInfo.sortDirection}
    &pageInfo.page=${gridFilterData.PageInfo.Page}&pageInfo.pageSize=${gridFilterData.PageInfo.PageSize}&includeSubLocations=${gridFilterData.IncludeSubLocations}
    &searchText=${gridFilterData.SearchText}`;
    return this.http.get<any>(url);
  }

  activateLocation(locationId: number): Observable<any> {
    const url = `${this.baseUrl}${this.apiController}/activate/${locationId}`;
    return this.http.put<any>(url, {});
  }

  deactivateLocation(locationId: number): Observable<any> {
    const url = `${this.baseUrl}${this.apiController}/deactivate/${locationId}`;
    return this.http.put<any>(url, {});
  }

  getLocationHierachyBySignedUser(homeLocationId: number, active: number = 1): Observable<SignedUserLocations> {
    return this.http.get<SignedUserLocations>(`${this.baseUrl}${this.apiController}/locationsHierachyBySignedUser/${homeLocationId}/${active}`, {});
  }

  getSelectorLocationHierachy(isDisableParentClick: boolean, isIncludeFloatLocation: boolean, hideBranchLess: boolean, includeInactive: boolean,isLobby:boolean=false): Observable<any> {
    return this.http.get<LocationSelector>(`${this.baseUrl}${this.apiController}/locationhierarchy?isDisableParentClick=${isDisableParentClick}&isIncludeFloatLocations=${isIncludeFloatLocation}&hideBranchLess=${hideBranchLess}&includeInactive=${includeInactive}&isLobby=${isLobby}`);
  }

  /**
   * Returns true if Location attribute exists, ie: INVALID
   * @param field The attribute to be validated, eg: LocationCode, LocationName, etc
   * @param value The value of the field
   * @returns a boolean
   */
  locationAttributeExists(locationId: number, field: string, value: string): Observable<boolean> {
    const queryParams = new HttpParams()
      .set('locationId', locationId)
      .set('field', field)
      .set('value', value);
    return this.http.get<boolean>(`${this.baseUrl}${this.apiController}/locationAttrExists`, { params: queryParams });
  }

  /**
   * Same as getSubLocations, but this does not take any query parameters
   * @param locationId The locationId for which to search the sub locations for
   * @returns A list of sub locations for the queried location
   */
  getSubLocationsForLocation(locationId: number): Observable<LocationRelationship[]> {
    return this.http.get<LocationRelationship[]>(`${this.baseUrl}${this.apiController}/${locationId}/subLocations`);
  }

  /**
   * Routes back to the location grid
   */
  gotoLocationGrid(): void {
    this.router.navigate(['admin/location/index']);
  }

  /**
   * Save Basic Location Settings
   * @param locationBasicSettings location basic settings
   * @returns Id of the created location
   */
  saveBasicLocationSettings(locationBasicSettings: Location): Observable<LocationCreatedResponse> {
    return this.http.post<LocationCreatedResponse>(`${this.baseUrl}${this.apiController}`, locationBasicSettings);
  }

  updateBasicLocationSettings(locationBasicSettings: Location): Observable<any> {
    return this.http.put<any>(`${this.baseUrl}${this.apiController}/location`, locationBasicSettings);
  }

  updateAppointmentSettings(location: Location, subLocationIds: number[]): Observable<any> {
    return this.http.put(`${this.baseUrl}${this.apiController}/appointments`, location, { params: { "subLocationIds": subLocationIds.map(String) } })
  }

  updateOperationalHoursSettings(operationalHours: OperatingHours[], subLocationIds: number[]): Observable<any> {
    return this.http.put(`${this.baseUrl}${this.apiController}/operationalHours`, operationalHours, { params: { "subLocationIds": subLocationIds.map(String) } })
  }

  updateLobbySettings(location: Location, subLocationIds: number[]): Observable<any> {
    return this.http.put(`${this.baseUrl}${this.apiController}/lobbysettings`, location, { params: { "subLocationIds": subLocationIds.map(String) } })
  }

  // Utilities
  // Conversion of ISO Alpha 3 country codes to ISO Alpha 2 (3 letter to 2 letter)
  alpha3Toalpha2(alpha3code: string): any {
    const alpha3Codes = ["AFG", "ALB", "DZA", "AND", "AGO", "AIA", "ATA", "ATG", "SAU", "ARG", "ARM", "ABW", "AUS", "AUT", "AZE", "BHS", "BHR", "BGD", "BRB", "BEL", "BLZ", "BEN", "BMU", "BTN", "BLR", "BOL", "BES", "BIH", "BWA", "BRA", "BRN", "IOT", "VGB", "BGR",
      "BFA", "BDI", "CHL", "CHN", "HRV", "CUW", "CYP", "TCD", "MNE", "CZE", "UMI", "DNK", "COD", "DMA", "DOM", "DJI", "EGY", "ECU", "ERI", "EST", "SWZ", "ETH", "FLK", "FJI", "PHL", "FIN", "FRA", "ATF", "GAB", "GMB", "SGS", "GHA", "GIB", "GRC", "GRD", "GRL", "GEO",
      "GUM", "GGY", "GUF", "GUY", "GLP", "GTM", "GNB", "GNQ", "GIN", "HTI", "ESP", "NLD", "HND", "HKG", "IND", "IDN", "IRQ", "IRN", "IRL", "ISL", "ISR", "JAM", "JPN", "YEM", "JEY", "JOR", "CYM", "KHM", "CMR", "CAN", "QAT", "KAZ", "KEN", "KGZ", "KIR", "COL", "COM",
      "COG", "KOR", "PRK", "CRI", "CUB", "KWT", "LAO", "LSO", "LBN", "LBR", "LBY", "LIE", "LTU", "LUX", "LVA", "MKD", "MDG", "MYT", "MAC", "MWI", "MDV", "MYS", "MLI", "MLT", "MNP", "MAR", "MTQ", "MRT", "MUS", "MEX", "FSM", "MMR", "MDA", "MCO", "MNG", "MSR", "MOZ",
      "NAM", "NRU", "NPL", "DEU", "NER", "NGA", "NIC", "NIU", "NFK", "NOR", "NCL", "NZL", "OMN", "PAK", "PLW", "PSE", "PAN", "PNG", "PRY", "PER", "PCN", "PYF", "POL", "PRI", "PRT", "ZAF", "CAF", "CPV", "REU", "RUS", "ROU", "RWA", "ESH", "KNA", "LCA", "VCT", "BLM",
      "MAF", "SPM", "SLV", "ASM", "WSM", "SMR", "SEN", "SRB", "SYC", "SLE", "SGP", "SXM", "SVK", "SVN", "SOM", "LKA", "USA", "SDN", "SSD", "SUR", "SJM", "SYR", "CHE", "SWE", "TJK", "THA", "TWN", "TZA", "TLS", "TGO", "TKL", "TON", "TTO", "TUN", "TUR", "TKM", "TCA",
      "TUV", "UGA", "UKR", "URY", "UZB", "VUT", "WLF", "VAT", "VEN", "HUN", "GBR", "VNM", "ITA", "CIV", "BVT", "CXR", "IMN", "SHN", "ALA", "COK", "VIR", "HMD", "CCK", "MHL", "FRO", "SLB", "STP", "ZMB", "ZWE", "ARE"];

    const alpha2Codes = ['AF', 'AL', 'DZ', 'AD', 'AO', 'AI', 'AQ', 'AG', 'SA', 'AR', 'AM', 'AW', 'AU', 'AT', 'AZ', 'BS', 'BH', 'BD', 'BB', 'BE', 'BZ', 'BJ', 'BM', 'BT', 'BY', 'BO', 'BQ', 'BA', 'BW', 'BR', 'BN', 'IO', 'VG', 'BG', 'BF', 'BI', 'CL', 'CN', 'HR', 'CW', 'CY',
      'TD', 'ME', 'CZ', 'UM', 'DK', 'CD', 'DM', 'DO', 'DJ', 'EG', 'EC', 'ER', 'EE', 'SZ', 'ET', 'FK', 'FJ', 'PH', 'FI', 'FR', 'TF', 'GA', 'GM', 'GS', 'GH', 'GI', 'GR', 'GD', 'GL', 'GE', 'GU', 'GG', 'GF', 'GY', 'GP', 'GT', 'GW', 'GQ', 'GN', 'HT', 'ES', 'NL', 'HN', 'HK', 'IN',
      'ID', 'IQ', 'IR', 'IE', 'IS', 'IL', 'JM', 'JP', 'YE', 'JE', 'JO', 'KY', 'KH', 'CM', 'CA', 'QA', 'KZ', 'KE', 'KG', 'KI', 'CO', 'KM', 'CG', 'KR', 'KP', 'CR', 'CU', 'KW', 'LA', 'LS', 'LB', 'LR', 'LY', 'LI', 'LT', 'LU', 'LV', 'MK', 'MG', 'YT', 'MO', 'MW', 'MV', 'MY', 'ML',
      'MT', 'MP', 'MA', 'MQ', 'MR', 'MU', 'MX', 'FM', 'MM', 'MD', 'MC', 'MN', 'MS', 'MZ', 'NA', 'NR', 'NP', 'DE', 'NE', 'NG', 'NI', 'NU', 'NF', 'NO', 'NC', 'NZ', 'OM', 'PK', 'PW', 'PS', 'PA', 'PG', 'PY', 'PE', 'PN', 'PF', 'pl', 'PR', 'PT', 'ZA', 'CF', 'CV', 'RE', 'RU', 'RO',
      'RW', 'EH', 'KN', 'LC', 'VC', 'BL', 'MF', 'PM', 'SV', 'AS', 'WS', 'SM', 'SN', 'RS', 'SC', 'SL', 'SG', 'SX', 'SK', 'SI', 'SO', 'LK', 'US', 'SD', 'SS', 'SR', 'SJ', 'SY', 'CH', 'SE', 'TJ', 'TH', 'TW', 'TZ', 'TL', 'TG', 'TK', 'TO', 'TT', 'TN', 'TR', 'TM', 'TC', 'TV', 'UG',
      'UA', 'UY', 'UZ', 'VU', 'WF', 'VA', 'VE', 'HU', 'GB', 'VN', 'IT', 'CI', 'BV', 'CX', 'IM', 'SH', 'AX', 'CK', 'VI', 'HM', 'CC', 'MH', 'FO', 'SB', 'ST', 'ZM', 'ZW', 'AE'];

    return alpha2Codes[alpha3Codes.indexOf(alpha3code)];
  }

    //Returns true if branch level locations have google reserve enabled
    locationsWithGoogleReserveExists(): Observable<boolean> {
      return this.http.get<boolean>(`${this.baseUrl}${this.apiController}/locationsWithGoogleReserveExists`);
    }
}
